import React, { useCallback, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDrag, useDrop } from 'react-dnd';

import { getBase64ImageInfo } from '@ecp/common/src/utils/fileUtil';
//import theme from '@ecp/common/src/style/theme/default';
import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import FileUploaderUnstyled, { FILE_SIZE } from '@ecp/common/src/components/unstyled/input/FileUploaderUnstyled';
import { DND_IMAGE_ICON } from '@ecp/common/src/const/imageFile';
import DeleteIcon from '@ecp/common/src/assets/icon/icon__fo__image-delete.svg';
import { ReactComponent as AddIcon } from '@ecp/common/src/assets/icon/icon__add.svg';

const ImageCard = React.forwardRef(
  (
    {
      value: imageFile,
      onChange,
      onError,
      onDelete,
      draggable,
      onMove,
      keyValue,
      width,
      height,
      extension,
      maxSize,
      ...props
    },
    ref
  ) => {
    const fileUploaderRef = useRef(null);
    useImperativeHandle(ref, () => fileUploaderRef.current.value);

    const handleChange = useCallback(
      async (e, fileList, result) => {
        if (result.errors.length) {
          e && (e.target.value = '');
          onError?.(result);
        } else {
          const file = fileList[0];
          const imgInfo = await getBase64ImageInfo(file);
          onChange?.({ ...imgInfo, file }, result);
        }
      },
      [onChange, onError]
    );

    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: DND_IMAGE_ICON,
        item: { type: keyValue },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
        end: async ({ type: dragType }, monitor) => {
          const didDrop = monitor.didDrop();
          if (!didDrop) {
            onMove(dragType, keyValue);
          }
        },
      }),
      [keyValue, onMove]
    );

    const [, drop] = useDrop(
      () => ({
        accept: DND_IMAGE_ICON,
        hover({ type: dragType }) {
          if (dragType !== keyValue) {
            onMove(dragType, keyValue);
          }
        },
      }),
      [onMove]
    );

    const handleDeleteClick = useCallback(() => {
      onDelete?.(keyValue);
    }, [keyValue, onDelete]);

    return (
      <ImageCardContainer ref={draggable ? (node) => drag(drop(node)) : undefined} opacity={isDragging ? 0 : 1}>
        {imageFile.url ? (
          <PreviewCard
            imageFile={imageFile}
            deleteButtonComponent={<DeleteButton onClick={handleDeleteClick} />}
            width={width}
            height={height}
            {...props}
          />
        ) : (
          <FileUploaderUnstyled
            buttonComponent={<EmptyCard width={width} height={height} />}
            onChange={handleChange}
            extension={extension}
            maxSize={maxSize}
            ref={fileUploaderRef}
          />
        )}
      </ImageCardContainer>
    );
  }
);

ImageCard.displayName = 'ImageCard';

ImageCard.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func,
  draggable: PropTypes.bool,
  onMove: PropTypes.func,
  keyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.string,
  height: PropTypes.string,
  extension: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
};

ImageCard.defaultProps = {
  draggable: false,
  width: '70px',
  height: '70px',
  maxSize: FILE_SIZE.IMAGE,
};

export default ImageCard;

export const EmptyCard = ({ width, height }) => {
  return (
    <DefaultButtonComponent width={width} height={height}>
      <AddIcon />
    </DefaultButtonComponent>
  );
};

EmptyCard.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
};

EmptyCard.defaultProps = {
  width: '70px',
  height: '70px',
};

const PreviewCard = ({ imageFile, deleteButtonComponent, width, height }) => {
  return (
    <>
      <FlexBoxColumn>
        <PreviewImageWrapper width={width} height={height}>
          <Image className="card-image" src={imageFile.url} alt={imageFile.url} />
          {deleteButtonComponent}
        </PreviewImageWrapper>
      </FlexBoxColumn>
    </>
  );
};

PreviewCard.propTypes = {
  imageFile: PropTypes.object,
  deleteButtonComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  width: PropTypes.string,
  height: PropTypes.string,
};

PreviewCard.defaultProps = {
  width: '70px',
  height: '70px',
};

export const ImageCardContainer = styled(FlexBoxColumn)`
  width: fit-content;
  height: fit-content;
  opacity: ${({ opacity }) => opacity};
`;

const DefaultButtonComponent = styled(FlexBoxColumn)`
  justify-content: center;
  padding: 4px 0;
  pointer-events: none;
  gap: 2px;

  font-size: ${(props) => props.theme.font.size.small};
  font-weight: 400;
  color: #000000;

  width: ${({ width }) => width};
  height: ${({ height }) => height};
  background: ${(props) => props.theme.color.background.white};
  border: 1px solid ${(props) => props.theme.color.line.background};
  border-radius: ${(props) => props.theme.component.imageFileUploader.borderRadius};
`;

export const PreviewImageWrapper = styled.div`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  border: 1px solid #dddddd;
  background-color: #eeeeee;
  border-radius: ${(props) => props.theme.component.imageFileUploader.borderRadius};

  & .card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;
  display: inline-block;
  width: 18px;
  height: 18px;
  background: url(${DeleteIcon}) no-repeat;
  font-size: 0;
  line-height: 0;
`;

const Image = styled.img`
  border-radius: ${(props) => props.theme.component.imageFileUploader.borderRadius};
`;
